import CameraControls from 'camera-controls'
import type { OrthographicCamera, PerspectiveCamera } from 'three'
import { Spherical, Vector3 } from 'three'

const DOLLY_DIRECTION = {
  NONE: 0,
  IN: 1,
  OUT: -1,
} as const

const EPSILON = 1e-5

function approxZero(number: number, error: number = EPSILON): boolean {
  return Math.abs(number) < error
}

function approxEquals(a: number, b: number, error: number = EPSILON): boolean {
  return approxZero(a - b, error)
}

export default class PatchedCameraControls extends CameraControls {
  constructor(camera: PerspectiveCamera | OrthographicCamera) {
    super(camera)
  }
  private _v3A = new Vector3()
  private _v3B = new Vector3()
  private _v3C = new Vector3()
  private _sphericalA = new Spherical()
  private _sphericalB = new Spherical()

  override lerpLookAt(
    positionAX: number,
    positionAY: number,
    positionAZ: number,
    targetAX: number,
    targetAY: number,
    targetAZ: number,
    positionBX: number,
    positionBY: number,
    positionBZ: number,
    targetBX: number,
    targetBY: number,
    targetBZ: number,
    t: number,
    enableTransition?: boolean | undefined
  ): Promise<void> {
    this._isUserControllingRotate = false
    this._isUserControllingDolly = false
    this._isUserControllingTruck = false
    this._lastDollyDirection = DOLLY_DIRECTION.NONE
    this._changedDolly = 0

    const targetA = this._v3A.set(targetAX, targetAY, targetAZ)
    const positionA = this._v3B.set(positionAX, positionAY, positionAZ)
    this._sphericalA.setFromVector3(positionA.sub(targetA).applyQuaternion(this._yAxisUpSpace))

    const targetB = this._v3C.set(targetBX, targetBY, targetBZ)
    const positionB = this._v3B.set(positionBX, positionBY, positionBZ)
    this._sphericalB.setFromVector3(positionB.sub(targetB).applyQuaternion(this._yAxisUpSpace))

    this._targetEnd.copy(targetA.lerp(targetB, t)) // tricky

    let deltaTheta = this._sphericalB.theta - this._sphericalA.theta
    const deltaPhi = this._sphericalB.phi - this._sphericalA.phi
    const deltaRadius = this._sphericalB.radius - this._sphericalA.radius

    if (Math.abs(deltaTheta) > Math.PI) {
      deltaTheta = deltaTheta > Math.PI ? deltaTheta - Math.PI * 2 : deltaTheta + Math.PI * 2
    }

    this._sphericalEnd.set(
      this._sphericalA.radius + deltaRadius * t,
      this._sphericalA.phi + deltaPhi * t,
      this._sphericalA.theta + deltaTheta * t
    )

    this.normalizeRotations()

    this._needsUpdate = true

    if (!enableTransition) {
      this._target.copy(this._targetEnd)
      this._spherical.copy(this._sphericalEnd)
    }

    const resolveImmediately =
      !enableTransition ||
      (approxEquals(this._target.x, this._targetEnd.x, this.restThreshold) &&
        approxEquals(this._target.y, this._targetEnd.y, this.restThreshold) &&
        approxEquals(this._target.z, this._targetEnd.z, this.restThreshold) &&
        approxEquals(this._spherical.theta, this._sphericalEnd.theta, this.restThreshold) &&
        approxEquals(this._spherical.phi, this._sphericalEnd.phi, this.restThreshold) &&
        approxEquals(this._spherical.radius, this._sphericalEnd.radius, this.restThreshold))

    return this._createOnRestPromise(resolveImmediately)
  }
}
